import React from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import AppBreadcrumbs, { BreadcrumbItem } from './AppBreadcrumbs';

interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  onAdd?: () => void;
  addLabel?: string;
  /** Zusätzliche Aktionen (z.B. Buttons) rechts neben onAdd */
  actions?: React.ReactNode;
  /** @deprecated Use breadcrumbs instead */
  backTo?: string;
  breadcrumbs?: BreadcrumbItem[];
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, onAdd, addLabel, actions, backTo, breadcrumbs }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  /* Build breadcrumbs from legacy backTo prop if no breadcrumbs provided */
  const effectiveBreadcrumbs = breadcrumbs || (backTo ? [
    { label: 'Zurück', path: backTo },
    { label: title },
  ] : undefined);

  return (
    <Box sx={{ mb: 3 }}>
      {effectiveBreadcrumbs && effectiveBreadcrumbs.length > 0 && (
        <AppBreadcrumbs items={effectiveBreadcrumbs} />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: accent,
              fontSize: { xs: '1.3rem', sm: '1.75rem' },
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Typography>
          {subtitle != null && subtitle !== '' && (
            typeof subtitle === 'string' ? (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mt: 0.3 }}
              >
                {subtitle}
              </Typography>
            ) : (
              <Box sx={{ mt: 0.3 }}>{subtitle}</Box>
            )
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {actions}
          {onAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAdd}
              sx={{
                bgcolor: accent,
                color: theme.palette.primary.contrastText,
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                px: { xs: 2, sm: 3 },
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: `0 0 20px ${accent}33`,
                },
              }}
            >
              {addLabel || 'Neu erstellen'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PageHeader;
